
import map from 'lodash/map';
import generateFunnelConfigPipeline from '@/reports/funnel/funnel-aggregator-builder/funnel-aggregator-builder';
import generateGroupingPipelines from '@/reports/funnel/grouping-builder/grouping-builder';
import { VISITOR_ROW_LIMIT, MAX_CARDINALITY_LIMIT } from '@/reports/funnel/utils';
import {
    FUNNEL_PRIMARY_KEY_TYPE,
    isAccountPrimaryKey,
    isVisitorPrimaryKey,
    resolveFunnelPrimaryKey,
} from '@/reports/funnel/funnel-primary-key.helper';
import { BuildProps } from '@/types/analytics/funnels.types';
import { operators as o, common as c } from '@pendo/aggregations';
import { MetadataTypeId } from '@/types/analytics/analytics.types';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { regionEvalExpression, regionEvalExpressionForFilter } from '@pendo/middleware';

function generateWholeFunnelMetricPipeline (config: c.PipelineFunnelData): c.PipelineData[] {
    return o.pipeline(
        o.pipeline(
            o.fork(
                o.pipeline(
                    o.group(['steps'], o.groupField('count', o.count(null))),
                    o.sort(['-steps']),
                    o.accumulate({ count: 'count' }),
                    o.group(
                        [],
                        o.groupField('counts', o.list('count')),
                        o.groupField('furthestStep', {
                            max: 'steps',
                        }),
                    ),
                    o.evalExpression(
                        'percentCompleted',
                        `if(furthestStep != ${config.items.length}, 0, (counts[0]/counts[len(counts) - 1]) * 100)`,
                    ),
                    o.select({
                        percentCompleted: 'percentCompleted',
                    }),
                ),
                o.pipeline(
                    o.compute({
                        durations: {
                            diff: 'times',
                        },
                    }),
                    o.filter(`len(durations) == ${config.items.length - 1}`),
                    o.group(
                        [],
                        o.groupField('averageTimes', o.listAverage('durations')),
                        o.groupField('medianTimes', o.listMedian('durations')),
                    ),
                    o.fork(
                        o.pipeline(
                            o.unwind('averageTimes'),
                            o.group([], o.groupField('averageTimeToCompletion', o.sum('averageTimes'))),
                        ),
                        o.pipeline(
                            o.unwind('medianTimes'),
                            o.group([], o.groupField('medianTimeToCompletion', o.sum('medianTimes'))),
                        ),
                    ),
                    o.join([]),
                ),
            ),
            o.join(),
            o.evaluate('rowType', '"wholeFunnelMetrics"'),
        ),
    );
}

function generateGroupInfoPipeline (groupKeys: string[], config: c.PipelineFunnelData) {
    const percentCompletedPipeline = o.pipeline(
        o.group(['steps', ...groupKeys], o.groupField('count', o.count(null))),
        o.group(groupKeys, o.groupField('groupSteps', o.accumulateAggregator(['-steps'], { count: 'count' }))),
        o.evalExpression(
            'percentCompleted',
            `if(groupSteps[0].steps != ${config.items.length}, 0, (groupSteps[0].count/groupSteps[len(groupSteps) - 1].count) * 100)`,
        ),
    );

    const timeToCompletionPipeline = o.pipeline(
        o.compute({
            durations: {
                diff: 'times',
            },
        }),
        o.filter(`len(durations) == ${config.items.length - 1}`),
        o.group(
            groupKeys,
            o.groupField('averageTimes', o.listAverage('durations')),
            o.groupField('medianTimes', o.listMedian('durations')),
        ),
        o.fork(
            o.pipeline(
                o.unwind('averageTimes'),
                o.group(groupKeys, o.groupField('averageTimeToCompletion', o.sum('averageTimes'))),
            ),
            o.pipeline(
                o.unwind('medianTimes'),
                o.group(groupKeys, o.groupField('medianTimeToCompletion', o.sum('medianTimes'))),
            ),
        ),
    );

    const averageAndMedianPipeline = o.pipeline(
        o.compute({
            durations: {
                diff: 'times',
            },
        }),
        o.group(
            groupKeys,
            o.groupField('averageTimes', o.listAverage('durations')),
            o.groupField('medianTimes', o.listMedian('durations')),
        ),
    );

    const groupOperators = [
        o.sort(['-percentCompleted']),
        o.limit(MAX_CARDINALITY_LIMIT + 1),
        o.fork(
            o.pipeline(o.limit(MAX_CARDINALITY_LIMIT), o.evaluate('rowType', '"groupInfo"')),
            o.pipeline(
                o.reduce({
                    groupCount: o.count(null),
                }),
                o.evaluate({
                    hasExceededMaxThresholdCount: `if(groupCount > ${MAX_CARDINALITY_LIMIT}, true, false)`,
                }),
                o.evaluate('rowType', '"funnelExceedsCardinality"'),
            ),
        ),
    ];

    const ungroupedOperator = [o.evaluate('rowType', '"groupInfo"')];

    return o.pipeline(
        o.fork(percentCompletedPipeline, timeToCompletionPipeline, averageAndMedianPipeline),
        o.join(groupKeys),
        ...(groupKeys.length > 0 ? groupOperators : ungroupedOperator),
    );
}

export default {
    name: 'funnel',
    productArea: PRODUCT_AREAS.ANALYTICS,
    subProductArea: SUB_PRODUCT_AREAS.FUNNELS,
    requestId: ({ report }: { report: { id: string } }) => `funnel-${report.id}`,
    response: ({
        forceRun,
        impersonating,
        report,
    }: {
        forceRun: boolean;
        impersonating: boolean;
        report: { id: string };
    }) => {
        return {
            name: report.id,
            location: 'report',
            mimeType: 'application/json',
            report,
            forceRun,
            impersonating,
        };
    },
    build: ({ groupings, timeSeries, config }: BuildProps) => {
        const includesTrackEvents = config.items.some((item) => !!item.trackTypeId);
        const includesAgentEvents = config.items.some((item) => !!item.agentId);

        const eventClass: string[] | undefined = [];
        if (includesTrackEvents) {
            eventClass.push('track');
            eventClass.push('ui');
        }
        if (includesAgentEvents) {
            eventClass.push('agentic');
            eventClass.push('ui');
        }

        const groupingPipelines = generateGroupingPipelines(groupings);

        const { funnelAggregatorConfig, hasDevicePropFilter, bulkExpand, hasRegionFilter } = generateFunnelConfigPipeline(
            config,
            groupings,
        );
        const hasDeviceGroupByInFunnel =
            groupings?.some((group) => group.type === MetadataTypeId.Automatic) ||
            groupings?.some((group) => group.configuration?.kind === MetadataTypeId.Automatic);
        const hasDevicePropsInFunnel = hasDevicePropFilter || hasDeviceGroupByInFunnel;

        const hasRegionGroupByInFunnel = groupings?.some((group) => group.configuration?.field === 'region');

        const bulkExpandFiltersOperators = bulkExpand.map((type) => o.bulkExpand(type));

        const { field: primaryKeyField, entityField: primaryKeyEntityField } = resolveFunnelPrimaryKey(config);
        // `primaryKeyField` is the source expression (e.g. `properties.reportId`).
        // `primaryKeyEntityField` is the top-level pipeline alias the funnel
        // op operates on — same as the source for `visitorId` / `accountId`,
        // but a synthetic name for nested paths (projected via evalExpression
        // below) since the BE pathAggregator panics on RawJsonRow inputs.
        const needsPrimaryKeyProjection = primaryKeyField !== primaryKeyEntityField;
        const isVisitorKey = isVisitorPrimaryKey(config);
        const isAccountKey = isAccountPrimaryKey(config);
        const objectPrimaryKey =
            config.primaryKey?.type === FUNNEL_PRIMARY_KEY_TYPE.OBJECT ? config.primaryKey : null;
        const isObjectKey = objectPrimaryKey !== null;
        // Re-keys the singleEvents source on the named ObjectProperty so it
        // emits typed *SingleEventRowWithMatching rows already sorted &
        // sharded by `properties.<name>`. Without this, an upstream sort step
        // on the property would force a shuffle that serializes rows to JSON,
        // and the funnel aggregator panics on RawJsonRow inputs. The source
        // re-adds the `properties.` prefix when building its SortKey, so we
        // pass the bare field (e.g. `reportId`, or the historical-metadata
        // path beyond `properties.`).
        const objectPropertySource = isObjectKey ? primaryKeyField.replace(/^properties\./, '') : undefined;
        // Floors event time at the moment the property was promoted to an
        // object property. Events from before promotion don't carry the value
        // reliably, so including them would inflate per-object counts.
        const objectPropertyTimestamp =
            typeof objectPrimaryKey?.property?.objectProperty === 'number'
                ? objectPrimaryKey.property.objectProperty
                : null;
        // For object-keyed funnels, leading the group keys with the
        // source-form field (e.g. `properties.reportId`) lets findSortedKeys
        // consume it as the key-sort prefix from the source's declared
        // sortedness, leaving inner-sort = [browserTime] — what
        // funnelAggregator.New requires. `primaryKeyEntityField` follows it
        // so the downstream `select` (and any references to `primaryKeyValue`)
        // keep working; the two are row-equal so this is a no-op for grouping.
        const baseGroupKeys = isObjectKey
            ? [primaryKeyField, primaryKeyEntityField]
            : [primaryKeyEntityField];
        const groupKeys =
            !isAccountKey && groupingPipelines.groupingByAccountMetadata
                ? [...baseGroupKeys, 'accountId']
                : baseGroupKeys;

        return o.build(
            o.settings(config.blacklist),
            o.pipeline(
                o.sources.singleEvents({
                    timeSeries: { ...timeSeries },
                    appId: config.appId,
                    eventClass: includesTrackEvents || includesAgentEvents ? eventClass : undefined,
                    _objectProperty: objectPropertySource,
                }),
                o.identified(config.includeAnonymous || undefined),
                o.segment(config.segmentId),
                hasDevicePropsInFunnel && o.userAgent('parsedUserAgent'),
                ...bulkExpandFiltersOperators,
                hasRegionGroupByInFunnel && o.evalExpression({ country_region: regionEvalExpression }),
                hasRegionFilter && o.evalExpression({ country_region: regionEvalExpressionForFilter }),
                // Project nested primary-key sources (e.g. `properties.X`) to
                // a top-level field before the group/funnel-op step; the
                // funnel op can't use a nested path as an entity key (the BE
                // materializes them to RawJsonRow which the pathAggregator
                // rejects).
                needsPrimaryKeyProjection && o.evalExpression({ [primaryKeyEntityField]: primaryKeyField }),
                // Object-keyed sources already restrict to events carrying the
                // property via `_objectProperty` on singleEvents above, so the
                // emptiness filter would never trim. Account-keyed sources
                // don't pre-filter on accountId presence, so the filter is
                // still load-bearing there.
                isAccountKey && o.filter(`!isEmpty(${primaryKeyEntityField})`),
                objectPropertyTimestamp !== null && o.filter(`time >= ${objectPropertyTimestamp}`),
                // singleEvents arrives sorted by [visitorId, browserTime].
                // The BE funnel/pathAggregator panics unless its per-group
                // inner sort is [browserTime], which requires the group key
                // to be the leading prefix of the dataflow sort. Object keys
                // get this via `_objectProperty` on the source above (which
                // re-keys the source's native sortedness — adding a pipeline
                // sort step would force a shuffle that serializes rows to
                // JSON, which the funnel aggregator can't consume). Account
                // keys aren't natively re-keyed, so we still need a sort step.
                isAccountKey && o.sort([primaryKeyEntityField, 'browserTime']),
                o.group(groupKeys, o.groupField('funnel', { funnel: { ...funnelAggregatorConfig } })),
                groupingPipelines.bulkExpand,
                o.unwind('funnel'),
                ...groupingPipelines.evals,
                o.select({
                    rowType: '"visitors"',
                    times: 'funnel.times',
                    start: 'funnel.start',
                    steps: 'funnel.steps',
                    // Carries the resolved primary key value (visitorId,
                    // accountId, or an object-property path) under a neutral
                    // name so downstream UI doesn't have to pretend an object
                    // id is a visitor id. For nested sources this reads from
                    // the top-level alias projected upstream.
                    primaryKeyValue: primaryKeyEntityField,
                    // accountIds is the only per-step entity-id list the BE
                    // funnel op exposes today, and it's only meaningful for
                    // visitor-keyed funnels (one visitor → one account per
                    // step). For non-visitor-keyed funnels we don't have a
                    // BE-supported way to surface per-step visitors yet — see
                    // FUNNELS-FOLLOWUP.md in this directory.
                    ...(isVisitorKey ? { accountIds: 'funnel.accountIds' } : {}),
                    recordingIds: 'funnel.recordingIds',
                    recordingSessionIds: 'funnel.recordingSessionIds',
                    tabIds: 'funnel.tabIds',
                    conversationId: 'funnel.conversationIds',
                    content: 'funnel.contents',
                    ...groupingPipelines.selects,
                }),
                o.fork(
                    generateWholeFunnelMetricPipeline(config),
                    generateGroupInfoPipeline(groupingPipelines.columns, config),
                    ...map(config.items, (_item, i) => [o.filter(`steps == ${i + 1}`), o.limit(VISITOR_ROW_LIMIT)]),
                ),
            ),
        );
    },
};
